import { useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Users, Receipt, LogOut, ArrowLeft, MessageSquare, Trash2, Mail, Reply, Send, CheckCircle2, Award, Download, Ban, ShieldCheck, UserCog, FileText } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";
import AdminProfile from "../components/AdminProfile";
import AdminArticles from "../components/AdminArticles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtMoney = (a, c) => `${(Number(a || 0) / 100).toLocaleString("fr-FR")} ${String(c || "eur").toUpperCase()}`;

export default function Admin() {
  const { user, logout } = useAuth();
  const [members, setMembers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [messages, setMessages] = useState([]);
  const [certs, setCerts] = useState([]);
  const [tab, setTab] = useState("messages");

  const loadCerts = async () => {
    try { const { data } = await axios.get(`${API}/admin/certificates`); setCerts(data || []); } catch (e) { /* silent */ }
  };

  const toggleCert = async (c) => {
    const action = c.status === "VALID" ? "revoke" : "restore";
    try { await axios.post(`${API}/admin/certificates/${c.certificate_number}/${action}`); loadCerts(); }
    catch (e) { /* silent */ }
  };
  const [replyFor, setReplyFor] = useState(null);
  const [replyBody, setReplyBody] = useState("");

  const loadMessages = async () => {
    try { const { data } = await axios.get(`${API}/contact`); setMessages(data || []); } catch (e) { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [m, p] = await Promise.all([
          axios.get(`${API}/admin/members`),
          axios.get(`${API}/admin/purchases`),
        ]);
        setMembers(m.data || []);
        setPurchases(p.data || []);
      } catch (e) { /* silent */ }
      loadMessages();
      loadCerts();
    })();
  }, []);

  const deleteMessage = async (id) => {
    try { await axios.delete(`${API}/contact/${id}`); setMessages((prev) => prev.filter((m) => m.id !== id)); }
    catch (e) { /* silent */ }
  };

  const startReply = (m) => {
    setReplyFor(m.id);
    setReplyBody(`Bonjour ${m.name},\n\nMerci pour votre message et l'intérêt porté à NovaLean Consulting.\n\n\n\nBien cordialement,\nMajdi Zarougui\nNovaLean Consulting — novalean26@outlook.fr`);
  };

  const sendReply = async (m, channel) => {
    const subject = `Re: Votre demande — NovaLean Consulting`;
    if (channel === "outlook") {
      window.open(`https://outlook.live.com/mail/0/deeplink/compose?to=${encodeURIComponent(m.email)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(replyBody)}`, "_blank");
    } else {
      window.location.href = `mailto:${m.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(replyBody)}`;
    }
    try {
      await axios.patch(`${API}/contact/${m.id}`);
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, replied: true } : x)));
    } catch (e) { /* silent */ }
    setReplyFor(null);
  };

  const revenue = purchases.reduce((s, p) => s + Number(p.amount || 0), 0);

  return (
    <>
      <header className="relative bg-ink-900 text-paper grain pt-36 pb-14 md:pt-44 md:pb-16 overflow-hidden" data-testid="admin-dashboard">
        <div className="absolute left-0 top-0 w-[40vw] h-[40vw] rounded-full bg-gold/10 blur-[120px]" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <span className="label-mono text-gold">Espace administrateur</span>
              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
                className="font-display text-5xl md:text-7xl font-semibold mt-4 tracking-tight leading-[0.9]">Pilotage.</motion.h1>
            </div>
            <div className="flex gap-3">
              <Link to="/mon-espace" className="inline-flex items-center gap-2 rounded-full border border-paper/30 px-5 py-3 label-mono hover:bg-paper hover:text-ink transition-colors"><ArrowLeft size={14} /> Mon espace</Link>
              <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-paper/30 px-5 py-3 label-mono hover:bg-paper hover:text-ink transition-colors"><LogOut size={14} /> Déconnexion</button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            {[["Messages", messages.length], ["Membres", members.length], ["Achats", purchases.length], ["Revenus", fmtMoney(revenue, "eur")]].map(([l, v]) => (
              <div key={l} className="border-t hairline-light pt-4">
                <div className="font-display text-3xl md:text-5xl font-semibold text-gold">{v}</div>
                <div className="label-mono text-steel mt-2">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </header>

      <section className="bg-paper py-14 md:py-20">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex flex-wrap gap-2 mb-8">
            <button onClick={() => setTab("messages")} data-testid="tab-messages"
              className={`inline-flex items-center gap-2 label-mono px-5 py-2.5 rounded-full border transition-colors ${tab === "messages" ? "bg-ink text-paper border-ink" : "hairline text-ink/70 hover:border-ink"}`}>
              <MessageSquare size={14} /> Messages {messages.length > 0 && <span className="bg-gold text-ink rounded-full min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold px-1">{messages.length}</span>}
            </button>
            <button onClick={() => setTab("members")} data-testid="tab-members"
              className={`inline-flex items-center gap-2 label-mono px-5 py-2.5 rounded-full border transition-colors ${tab === "members" ? "bg-ink text-paper border-ink" : "hairline text-ink/70 hover:border-ink"}`}>
              <Users size={14} /> Membres
            </button>
            <button onClick={() => setTab("purchases")} data-testid="tab-purchases"
              className={`inline-flex items-center gap-2 label-mono px-5 py-2.5 rounded-full border transition-colors ${tab === "purchases" ? "bg-ink text-paper border-ink" : "hairline text-ink/70 hover:border-ink"}`}>
              <Receipt size={14} /> Achats
            </button>
            <button onClick={() => setTab("certificats")} data-testid="tab-certificats"
              className={`inline-flex items-center gap-2 label-mono px-5 py-2.5 rounded-full border transition-colors ${tab === "certificats" ? "bg-ink text-paper border-ink" : "hairline text-ink/70 hover:border-ink"}`}>
              <Award size={14} /> Certificats
            </button>
            <button onClick={() => setTab("profil")} data-testid="tab-profil"
              className={`inline-flex items-center gap-2 label-mono px-5 py-2.5 rounded-full border transition-colors ${tab === "profil" ? "bg-ink text-paper border-ink" : "hairline text-ink/70 hover:border-ink"}`}>
              <UserCog size={14} /> Profil
            </button>
            <button onClick={() => setTab("articles")} data-testid="tab-articles"
              className={`inline-flex items-center gap-2 label-mono px-5 py-2.5 rounded-full border transition-colors ${tab === "articles" ? "bg-ink text-paper border-ink" : "hairline text-ink/70 hover:border-ink"}`}>
              <FileText size={14} /> Articles
            </button>
          </div>

          <Reveal>
            {tab === "profil" ? (
              <AdminProfile />
            ) : tab === "articles" ? (
              <AdminArticles />
            ) : (
            <div className="rounded-sm border hairline bg-white overflow-hidden overflow-x-auto">
              {tab === "messages" ? (
                <table className="w-full text-left" data-testid="messages-table">
                  <thead className="label-mono text-ink/50 border-b hairline">
                    <tr>
                      <th className="px-6 py-4">Date</th><th className="px-6 py-4">Nom</th>
                      <th className="px-6 py-4">Email</th><th className="px-6 py-4">Société / Tél.</th>
                      <th className="px-6 py-4">Message</th><th className="px-6 py-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((m) => (
                      <Fragment key={m.id}>
                      <tr className="border-b hairline last:border-0 hover:bg-paper/60 transition-colors align-top" data-testid={`message-row-${m.id}`}>
                        <td className="px-6 py-4 text-ink/60 whitespace-nowrap">{fmtDate(m.created_at)}</td>
                        <td className="px-6 py-4 font-semibold">{m.name} {m.replied && <span className="label-mono text-green-600 bg-green-100 px-2 py-0.5 rounded-full ml-2 inline-flex items-center gap-1"><CheckCircle2 size={10} /> Répondu</span>}</td>
                        <td className="px-6 py-4"><a href={`mailto:${m.email}`} className="text-gold hover:underline inline-flex items-center gap-1.5"><Mail size={13} />{m.email}</a></td>
                        <td className="px-6 py-4 text-ink/70">{[m.company, m.phone].filter(Boolean).join(" · ") || "—"}</td>
                        <td className="px-6 py-4 text-ink/70 max-w-[320px]"><span className="line-clamp-3">{m.message}</span></td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button onClick={() => (replyFor === m.id ? setReplyFor(null) : startReply(m))} data-testid={`reply-message-${m.id}`} aria-label="Répondre"
                              className="w-8 h-8 rounded-full border hairline flex items-center justify-center text-ink/40 hover:text-gold hover:border-gold transition-colors">
                              <Reply size={13} />
                            </button>
                            <button onClick={() => deleteMessage(m.id)} data-testid={`delete-message-${m.id}`} aria-label="Supprimer"
                              className="w-8 h-8 rounded-full border hairline flex items-center justify-center text-ink/40 hover:text-red-500 hover:border-red-300 transition-colors">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {replyFor === m.id && (
                        <tr key={`${m.id}-reply`} className="border-b hairline bg-paper/50" data-testid={`reply-panel-${m.id}`}>
                          <td colSpan={6} className="px-6 py-5">
                            <div className="max-w-2xl">
                              <p className="label-mono text-ink/40 mb-3">Répondre à {m.name} &lt;{m.email}&gt;</p>
                              <textarea value={replyBody} onChange={(e) => setReplyBody(e.target.value)} rows={7} data-testid={`reply-body-${m.id}`}
                                className="w-full bg-white border hairline rounded-sm px-4 py-3 text-sm outline-none focus:border-gold resize-y" />
                              <div className="flex flex-wrap gap-2 mt-3">
                                <button onClick={() => sendReply(m, "outlook")} data-testid={`reply-send-outlook-${m.id}`}
                                  className="inline-flex items-center gap-2 rounded-full bg-gold text-ink px-5 py-2.5 label-mono hover:bg-ink hover:text-paper transition-colors">
                                  <Send size={13} /> Envoyer via Outlook
                                </button>
                                <button onClick={() => sendReply(m, "mailto")} data-testid={`reply-send-mailto-${m.id}`}
                                  className="inline-flex items-center gap-2 rounded-full border hairline px-5 py-2.5 label-mono text-ink hover:bg-ink hover:text-paper transition-colors">
                                  <Mail size={13} /> Ma messagerie par défaut
                                </button>
                                <button onClick={() => setReplyFor(null)} className="label-mono text-ink/50 px-3 py-2.5 hover:text-ink transition-colors">Annuler</button>
                              </div>
                              <p className="text-ink/40 text-xs mt-3">Le message s'ouvre pré-rempli dans votre messagerie — il partira de votre adresse novalean26@outlook.fr. La demande sera marquée « Répondu ».</p>
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                    {messages.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-ink/50">Aucun message pour le moment.</td></tr>}
                  </tbody>
                </table>
              ) : tab === "certificats" ? (
                <table className="w-full text-left" data-testid="certs-table">
                  <thead className="label-mono text-ink/50 border-b hairline">
                    <tr>
                      <th className="px-6 py-4">N° certificat</th><th className="px-6 py-4">Membre</th>
                      <th className="px-6 py-4">Formation</th><th className="px-6 py-4">Score</th>
                      <th className="px-6 py-4">Date</th><th className="px-6 py-4">Statut</th><th className="px-6 py-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {certs.map((c) => (
                      <tr key={c.certificate_number} className="border-b hairline last:border-0 hover:bg-paper/60 transition-colors" data-testid={`admin-cert-${c.certificate_number}`}>
                        <td className="px-6 py-4 font-mono-tech text-sm">{c.certificate_number}</td>
                        <td className="px-6 py-4"><div className="font-semibold">{c.user_name}</div><div className="text-ink/50 text-xs">{c.user_email}</div></td>
                        <td className="px-6 py-4">{c.course_name}</td>
                        <td className="px-6 py-4 font-mono-tech text-gold">{c.qcm_score} %</td>
                        <td className="px-6 py-4 text-ink/60 whitespace-nowrap">{c.completion_date}</td>
                        <td className="px-6 py-4">{c.status === "VALID"
                          ? <span className="label-mono text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full inline-flex items-center gap-1.5"><ShieldCheck size={11} /> Valide</span>
                          : <span className="label-mono text-red-600 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">Révoqué</span>}</td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <a href={`${API}/certificates/${c.certificate_number}/pdf`} data-testid={`admin-cert-dl-${c.certificate_number}`} aria-label="Télécharger"
                              className="w-8 h-8 rounded-full border hairline flex items-center justify-center text-ink/40 hover:text-gold hover:border-gold transition-colors"><Download size={13} /></a>
                            <button onClick={() => toggleCert(c)} data-testid={`admin-cert-toggle-${c.certificate_number}`} aria-label={c.status === "VALID" ? "Révoquer" : "Restaurer"}
                              className={`w-8 h-8 rounded-full border hairline flex items-center justify-center transition-colors ${c.status === "VALID" ? "text-ink/40 hover:text-red-500 hover:border-red-300" : "text-ink/40 hover:text-green-600 hover:border-green-300"}`}>
                              {c.status === "VALID" ? <Ban size={13} /> : <ShieldCheck size={13} />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {certs.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-ink/50">Aucun certificat généré pour le moment.</td></tr>}
                  </tbody>
                </table>
              ) : tab === "members" ? (
                <table className="w-full text-left" data-testid="members-table">
                  <thead className="label-mono text-ink/50 border-b hairline">
                    <tr>
                      <th className="px-6 py-4">Nom</th><th className="px-6 py-4">Email</th>
                      <th className="px-6 py-4">Rôle</th><th className="px-6 py-4">Achats</th><th className="px-6 py-4">Inscrit le</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.id} className="border-b hairline last:border-0 hover:bg-paper/60 transition-colors">
                        <td className="px-6 py-4 font-semibold">{m.name || "—"}</td>
                        <td className="px-6 py-4 text-ink/70">{m.email}</td>
                        <td className="px-6 py-4"><span className={`label-mono px-2.5 py-1 rounded-full ${m.role === "admin" ? "bg-gold/20 text-gold" : "bg-ink/10 text-ink/70"}`}>{m.role}</span></td>
                        <td className="px-6 py-4 font-mono-tech">{m.purchases}</td>
                        <td className="px-6 py-4 text-ink/60">{fmtDate(m.created_at)}</td>
                      </tr>
                    ))}
                    {members.length === 0 && <tr><td colSpan={5} className="px-6 py-12 text-center text-ink/50">Aucun membre.</td></tr>}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-left" data-testid="purchases-table">
                  <thead className="label-mono text-ink/50 border-b hairline">
                    <tr>
                      <th className="px-6 py-4">Client</th><th className="px-6 py-4">Formation</th>
                      <th className="px-6 py-4">Montant</th><th className="px-6 py-4">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((p, i) => (
                      <tr key={i} className="border-b hairline last:border-0 hover:bg-paper/60 transition-colors">
                        <td className="px-6 py-4 text-ink/70">{p.customer_email}</td>
                        <td className="px-6 py-4 font-semibold">{p.product_name}</td>
                        <td className="px-6 py-4 font-mono-tech text-gold">{fmtMoney(p.amount, p.currency)}</td>
                        <td className="px-6 py-4 text-ink/60">{fmtDate(p.purchased_at)}</td>
                      </tr>
                    ))}
                    {purchases.length === 0 && <tr><td colSpan={4} className="px-6 py-12 text-center text-ink/50">Aucun achat.</td></tr>}
                  </tbody>
                </table>
              )}
            </div>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
